/**
 * Get panel height from current rendered layout.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @return {number} Panel height in pixels.
 */
const getPanelHeight = ( panel ) => panel.getBoundingClientRect().height;
const shouldUseInstantTransition = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Resolve accordion behavior mode.
 *
 * @param {HTMLElement} accordion Accordion root element.
 * @return {'single'|'multiple'} Behavior mode.
 */
const getAccordionBehavior = ( accordion ) => {

	return accordion.dataset.wombatFaqBehavior === 'multiple' ? 'multiple' : 'single';

};


/**
 * Clear pending panel animation state.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @return {void}
 */
const clearPanelAnimationState = ( panel ) => {

	if ( panel.__wombatFaqRafId ) {

		window.cancelAnimationFrame( panel.__wombatFaqRafId );
		panel.__wombatFaqRafId = null;

	}

	if ( panel.__wombatFaqTransitionEndHandler ) {

		panel.removeEventListener( 'transitionend', panel.__wombatFaqTransitionEndHandler );
		panel.__wombatFaqTransitionEndHandler = null;

	}

};

/**
 * Animate panel to target height.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @param {number} targetHeight Target height in px.
 * @param {Function} onEnd End callback.
 * @param {boolean} instant Whether to skip transition.
 * @param {string|null} targetPaddingBottom Target bottom padding value.
 * @return {void}
 */
const animatePanelHeight = ( panel, targetHeight, onEnd, instant = false, targetPaddingBottom = null ) => {

	clearPanelAnimationState( panel );
	const startHeight = getPanelHeight( panel );
	panel.style.height = `${startHeight}px`;

	if ( instant ) {

		panel.style.height = `${targetHeight}px`;
		if ( targetPaddingBottom !== null ) {

			panel.style.paddingBottom = targetPaddingBottom;

		}
		onEnd();
		return;

	}

	panel.__wombatFaqRafId = window.requestAnimationFrame( () => {

		panel.style.height = `${targetHeight}px`;
		if ( targetPaddingBottom !== null ) {

			panel.style.paddingBottom = targetPaddingBottom;

		}

	} );

	/**
	 * Finalize panel height transition.
	 *
	 * @param {TransitionEvent} event Transition event payload.
	 * @return {void}
	 */
	const handleTransitionEnd = ( event ) => {

		if ( event.propertyName !== 'height' ) {

			return;

		}

		clearPanelAnimationState( panel );
		onEnd();

	};

	panel.__wombatFaqTransitionEndHandler = handleTransitionEnd;
	panel.addEventListener( 'transitionend', handleTransitionEnd );

};

/**
 * Expand selected accordion panel.
 *
 * @param {HTMLElement} item Accordion item.
 * @param {HTMLElement|null} panel Accordion panel.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const openPanel = ( item, panel, instant = false ) => {

	if ( !panel ) {

		return;

	}

	panel.hidden = false;
	panel.removeAttribute( 'aria-hidden' );
	panel.inert = false;
	item.classList.add( 'is-active' );

	panel.style.transition = 'none';
	panel.style.height = 'auto';
	panel.style.paddingBottom = '';
	const expandedHeight = getPanelHeight( panel );
	const expandedPaddingBottom = window.getComputedStyle( panel ).paddingBottom;
	panel.style.height = '0px';
	panel.style.paddingBottom = '0px';
	panel.getBoundingClientRect();
	panel.style.transition = '';

	animatePanelHeight(
		panel,
		expandedHeight,
		() => {

			panel.style.height = 'auto';
			panel.style.paddingBottom = '';

		},
		instant,
		expandedPaddingBottom
	);

};

/**
 * Collapse non-active accordion panel.
 *
 * @param {HTMLElement} item Accordion item.
 * @param {HTMLElement|null} panel Accordion panel.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const closePanel = ( item, panel, instant = false ) => {

	if ( !panel ) {

		return;

	}

	panel.hidden = false;
	clearPanelAnimationState( panel );

	panel.style.transition = 'none';
	panel.style.height = `${getPanelHeight( panel )}px`;
	panel.style.paddingBottom = window.getComputedStyle( panel ).paddingBottom;
	panel.getBoundingClientRect();
	panel.style.transition = '';

	item.classList.remove( 'is-active' );

	animatePanelHeight(
		panel,
		0,
		() => {

			panel.style.height = '0px';
			panel.style.paddingBottom = '';
			panel.hidden = true;
			panel.setAttribute( 'aria-hidden', 'true' );
			panel.inert = true;

		},
		instant,
		'0px'
	);

};

/**
 * Activate currently selected FAQ item.
 *
 * @param {HTMLElement[]} items FAQ items.
 * @param {string} activeId Active item id.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const activateItem = ( items, activeId, instant = false ) => {

	items.forEach( ( item ) => {

		const itemId = item.dataset.itemId || '';
		const trigger = item.querySelector( '[data-wombat-faq-trigger]' );
		const panel = item.querySelector( '[data-wombat-faq-panel]' );
		const isActive = !!activeId && itemId === activeId;

		if ( trigger ) {

			trigger.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );

		}

		if ( isActive ) {

			openPanel( item, panel, instant );
			return;

		}

		closePanel( item, panel, instant );

	} );

};

/**
 * Toggle one accordion item (multiple-open mode).
 *
 * @param {HTMLElement} item Accordion item.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const toggleItem = ( item, instant = false ) => {

	const trigger = item.querySelector( '[data-wombat-faq-trigger]' );

	if ( !trigger ) {

		return;

	}

	const panel = item.querySelector( '[data-wombat-faq-panel]' );
	const isActive = item.classList.contains( 'is-active' );

	if ( isActive ) {

		trigger.setAttribute( 'aria-expanded', 'false' );
		closePanel( item, panel, instant );
		return;

	}

	trigger.setAttribute( 'aria-expanded', 'true' );
	openPanel( item, panel, instant );

};

document.querySelectorAll( '[data-wombat-faq]' ).forEach( ( accordion, accordionIndex ) => {

	const items = Array.from( accordion.querySelectorAll( '[data-wombat-faq-item]' ) );

	if ( !items.length ) {

		return;

	}

	const behavior = getAccordionBehavior( accordion );

	accordion.classList.add( 'is-enhanced' );

	items.forEach( ( item, index ) => {

		const itemId = `faq-${accordionIndex + 1}-item-${index + 1}`;
		const trigger = item.querySelector( '[data-wombat-faq-trigger]' );

		item.dataset.itemId = itemId;

		if ( !trigger ) {

			return;

		}

		const panel = item.querySelector( '[data-wombat-faq-panel]' );

		if ( panel ) {

			panel.id = `wombat-faq-panel-${itemId}`;
			trigger.setAttribute( 'aria-controls', panel.id );

		}

		trigger.addEventListener( 'click', () => {

			if ( behavior === 'multiple' ) {

				toggleItem( item, shouldUseInstantTransition );
				return;

			}

			if ( item.classList.contains( 'is-active' ) ) {

				return;

			}

			activateItem( items, itemId, shouldUseInstantTransition );

		} );

		trigger.addEventListener( 'keydown', ( event ) => {

			const triggerElements = items
				.map( ( accordionItem ) => accordionItem.querySelector( '[data-wombat-faq-trigger]' ) )
				.filter( Boolean );
			const triggerIndex = triggerElements.indexOf( trigger );
			const isLast = triggerIndex === triggerElements.length - 1;
			const isFirst = triggerIndex === 0;

			if ( event.key === 'ArrowDown' ) {

				event.preventDefault();
				const nextTrigger = isLast ? triggerElements[0] : triggerElements[triggerIndex + 1];
				nextTrigger?.focus();
				return;

			}

			if ( event.key === 'ArrowUp' ) {

				event.preventDefault();
				const previousTrigger = isFirst ? triggerElements[triggerElements.length - 1] : triggerElements[triggerIndex - 1];
				previousTrigger?.focus();
				return;

			}

			if ( event.key === 'Home' ) {

				event.preventDefault();
				triggerElements[0]?.focus();
				return;

			}

			if ( event.key === 'End' ) {

				event.preventDefault();
				triggerElements[triggerElements.length - 1]?.focus();

			}

		} );

	} );

	activateItem( items, items[0].dataset.itemId || '', true );

} );
